<script setup lang="ts">
import { hasOptOutCookie, setTrackingOptOut } from '~/utils/tracking-consent';

useHead({
  title: 'Your Privacy Choices — Arvee Events',
  meta: [
    { name: 'robots', content: 'noindex, follow' },
    {
      name: 'description',
      content:
        'Manage how Arvee Events shares your personal information for advertising. Opt out of the "sale" or "sharing" of your data under California law (CCPA/CPRA).',
    },
  ],
});

// The opt-out cookie is client-only, so the toggle's initial state is read
// after mount to avoid an SSR/hydration mismatch. Both server render and the
// initial client hydration see `false`; `onMounted` (which runs after
// hydration) then reflects the user's actual choice.
const optedOut = ref(false);
const gpcEnabled = ref(false);

// Tracks the most recent user action so we can show the right confirmation
// message. `null` means the user has not toggled the control this session.
const lastAction = ref<'opted-out' | 'opted-in' | null>(null);

onMounted(() => {
  optedOut.value = hasOptOutCookie();
  gpcEnabled.value =
    typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
});

function onToggle(value: boolean | null): void {
  const next = value === true;
  optedOut.value = next;
  setTrackingOptOut(next);
  lastAction.value = next ? 'opted-out' : 'opted-in';
}

function reloadPage(): void {
  if (import.meta.client) {
    window.location.reload();
  }
}
</script>

<template>
  <div>
    <h1 class="text-h4 mb-2">Your Privacy Choices</h1>
    <p class="text-body-2 text-medium-emphasis mb-6">
      California residents (CCPA/CPRA)
    </p>

    <section class="mb-8">
      <p class="text-body-1 mb-3">
        Arvee Events uses a third-party advertising pixel (Meta) that may share
        limited information about your visit with that advertising partner.
        Under California law (the CCPA/CPRA), this sharing may be considered a
        "sale" or "share" of personal information.
      </p>
      <p class="text-body-1 mb-3">
        Use the control below to opt out of that sharing. Your choice is stored
        on this browser and applies to this browser and device only — if you use
        another browser or device, or clear your cookies, you will need to set
        it again.
      </p>
      <p class="text-body-1 mb-3">
        Arvee Events also automatically honors the
        <strong>Global Privacy Control (GPC)</strong> browser signal. If your
        browser or an extension sends that signal, advertising trackers are
        disabled without any action on your part.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">Advertising opt-out</h2>

      <v-alert
        v-if="gpcEnabled"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mb-4"
      >
        Your browser's Global Privacy Control signal is enabled, so advertising
        trackers are already disabled.
      </v-alert>

      <v-switch
        :model-value="optedOut"
        label="Do not sell or share my personal information"
        color="primary"
        inset
        hide-details
        @update:model-value="onToggle"
      />

      <v-alert
        v-if="lastAction === 'opted-out'"
        type="success"
        variant="tonal"
        density="comfortable"
        class="mt-4"
      >
        <p class="mb-3">
          You have opted out. Advertising trackers will not load on future page
          views. Any tracker already loaded on this page cannot be removed until
          the page is reloaded — reload now to stop it fully.
        </p>
        <v-btn color="primary" variant="flat" size="small" @click="reloadPage">
          Reload page
        </v-btn>
      </v-alert>

      <v-alert
        v-else-if="lastAction === 'opted-in'"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mt-4"
      >
        You have opted back in. This takes effect the next time you navigate or
        reload the page.
      </v-alert>
    </section>

    <section>
      <h2 class="text-h5 mb-3">Learn more</h2>
      <p class="text-body-1 mb-3">
        For details on how we collect, use, and protect your information, see
        our
        <NuxtLink to="/privacy">Privacy Policy</NuxtLink>. Questions? Email us
        at <a href="mailto:support@arvee.io">support@arvee.io</a>.
      </p>
    </section>
  </div>
</template>
